import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { useState } from "react";

import { STRIPE_PUBLISHABLE_KEY } from "../env";

/**
 * Loaded once and reused for every mount, same convention as
 * `stripe-checkout-form.tsx` (Stripe's own recommendation — calling
 * `loadStripe` repeatedly re-injects its script tag for no benefit). Kept
 * as its own module-level instance (not imported from
 * `stripe-checkout-form.tsx`) so this component stays independently
 * testable, matching that file's own comment on the same pattern.
 */
let stripePromise: Promise<Stripe | null> | undefined;
function getStripePromise(): Promise<Stripe | null> {
  if (!stripePromise) {
    stripePromise = STRIPE_PUBLISHABLE_KEY
      ? loadStripe(STRIPE_PUBLISHABLE_KEY)
      : Promise.resolve(null);
  }
  return stripePromise;
}

export interface UpdatePaymentMethodDialogState {
  isOpen: boolean;
  /** The Stripe SetupIntent client secret returned by
   * `POST /subscriptions/update-payment-method` (#2795). Unlike
   * `StripeCheckoutForm`'s PaymentIntent-based `clientSecret`, confirming
   * this one never charges anything — it only validates and saves a new
   * card, per the UX/UI design spec's "SetupIntent flow this time, since
   * no immediate charge is happening" (flow e). */
  clientSecret?: string;
  isProcessing?: boolean;
  errorMessage?: string;
  language: SupportedLanguage;
}

export type UpdatePaymentMethodDialogEvent =
  | "close-update-payment-method-dialog"
  | "submit-payment-method-update"
  | "payment-method-update-succeeded"
  | "payment-method-update-failed";

export interface UpdatePaymentMethodDialogPayload {
  paymentMethodId?: string;
  message?: string;
}

/**
 * "Update payment method" dialog (#2795), opened from
 * `PastDuePaymentBanner`'s inline CTA. Same visual chrome/mounting pattern
 * as `StripeCheckoutForm` (flow a), but driven by a SetupIntent instead of
 * a PaymentIntent — see that component's own doc comment for why card data
 * never touches this component or any other application code.
 */
export const UpdatePaymentMethodDialog: PimoReactComponent<
  UpdatePaymentMethodDialogState,
  UpdatePaymentMethodDialogEvent,
  UpdatePaymentMethodDialogPayload
> = ({
  fireEvent,
  isOpen,
  clientSecret,
  isProcessing,
  errorMessage,
  language,
}) => {
  return (
    <Dialog
      open={isOpen}
      onClose={() => fireEvent?.("close-update-payment-method-dialog")}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
        {getLocalizedMessage(language, "updatePaymentMethodDialog.title")}
      </DialogTitle>
      <Typography variant="body2" color="text.secondary" sx={{ px: 3, pb: 1 }}>
        {getLocalizedMessage(language, "updatePaymentMethodDialog.subtitle")}
      </Typography>
      <DialogContent sx={{ pt: 1 }}>
        {clientSecret ? (
          <Elements
            stripe={getStripePromise()}
            options={{ clientSecret }}
            key={clientSecret}
          >
            <UpdatePaymentMethodFormInner
              fireEvent={fireEvent}
              isProcessing={!!isProcessing}
              errorMessage={errorMessage}
              language={language}
            />
          </Elements>
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

interface UpdatePaymentMethodFormInnerProps {
  fireEvent?: (
    event: UpdatePaymentMethodDialogEvent,
    payload?: UpdatePaymentMethodDialogPayload
  ) => void;
  isProcessing: boolean;
  errorMessage?: string;
  language: SupportedLanguage;
}

/**
 * Split out from `UpdatePaymentMethodDialog` because `useStripe`/
 * `useElements` only work inside an `<Elements>` provider — same reason
 * `StripeCheckoutFormInner` is split out in `stripe-checkout-form.tsx`.
 * This is the component that actually calls `stripe.confirmSetup`.
 */
function UpdatePaymentMethodFormInner({
  fireEvent,
  isProcessing,
  errorMessage,
  language,
}: UpdatePaymentMethodFormInnerProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!stripe || !elements || isProcessing) return;

    fireEvent?.("submit-payment-method-update", {});

    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      // Embedded, no hosted redirect — same rationale as
      // `StripeCheckoutForm`'s PaymentIntent confirmation: 3-D
      // Secure/SCA for card payment methods is handled inline.
      redirect: "if_required",
      confirmParams: { return_url: window.location.href },
    });

    if (error) {
      const message =
        error.type === "card_error" || error.type === "validation_error"
          ? (error.message ??
            getLocalizedMessage(language, "updatePaymentMethodDialog.declined"))
          : getLocalizedMessage(
              language,
              "updatePaymentMethodDialog.genericError"
            );
      fireEvent?.("payment-method-update-failed", { message });
      return;
    }

    if (setupIntent && setupIntent.status === "succeeded") {
      // `setupIntent.payment_method` is the confirmed PaymentMethod's id
      // (a plain string, not expanded) — never raw card data. The caller
      // (`home-portal-plugin.ts`) uses it to call
      // `POST /subscriptions/update-payment-method/confirm`, which sets it
      // as the Stripe customer's default for future invoice retries.
      const paymentMethodId =
        typeof setupIntent.payment_method === "string"
          ? setupIntent.payment_method
          : undefined;
      setShowSuccess(true);
      setTimeout(() => {
        fireEvent?.("payment-method-update-succeeded", { paymentMethodId });
      }, 1500);
    }
  };

  if (showSuccess) {
    return (
      <Alert severity="success">
        {getLocalizedMessage(language, "updatePaymentMethodDialog.success")}
      </Alert>
    );
  }

  return (
    <Box>
      <PaymentElement options={{ layout: "tabs" }} />

      {errorMessage ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errorMessage}
        </Alert>
      ) : null}

      <DialogActions sx={{ px: 0, pt: 2 }}>
        <Button
          onClick={() => fireEvent?.("close-update-payment-method-dialog")}
          disabled={isProcessing}
          sx={{ textTransform: "none" }}
        >
          {getLocalizedMessage(language, "updatePaymentMethodDialog.cancel")}
        </Button>
        <Button
          variant="contained"
          fullWidth
          disabled={!stripe || !elements || isProcessing}
          onClick={handleSubmit}
          sx={{
            bgcolor: "#ff8c00",
            color: "#fff",
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { bgcolor: "#e67e00" },
            "&.Mui-disabled": { bgcolor: "#f0d4b3", color: "#fff" },
          }}
          startIcon={
            isProcessing ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {isProcessing
            ? getLocalizedMessage(language, "updatePaymentMethodDialog.saving")
            : getLocalizedMessage(language, "updatePaymentMethodDialog.save")}
        </Button>
      </DialogActions>
    </Box>
  );
}
